<script setup lang="ts">
// ================================
// LE CHIFFRE (DEV-1913, décisions A3 et A4 ; D27 du 05/10/2026).
//
// D27 (maquettes 2 et 2 bis) : l'équivalent annuel du moteur passait au premier
// niveau (remplacé le 07/10, voir « Finitions » ci-dessous) ; et quand le conseil prudent coûte
// plus cher qu'aujourd'hui alors que l'autre scénario est rentable, le titre
// annonce l'économie possible et sa condition (règle dans
// `utils/optim-turpe-v2.titre.ts`). Le conseil prudent reste au temps 2.
//
// Ce que le client doit vivre : un seul montant en tête, l'économie NETTE sur la
// période analysée, avec sa durée, toujours décomposée en « structure (ferme,
// démontrable) » et « dépassements évités ou provoqués (à confirmer) ».
//
// Maquette allégée (07/10/2026) : au repos, le montant seul et ce qu'il
// contient (CTA). La décomposition et le seuil vivent sous « D'où vient ce
// montant » (tableau des postes), la mesure sur la ligne « Mesure » de la
// conclusion, la lecture des dépassements au temps 2 ; « Hors TVA » redisait
// « HT » et la période s'écrit aux paramètres.
//
// Finitions de l'étude allégée (07/10/2026) : une seule unité de montant sur
// la page. Le montant se lit « +2 821 € HT sur 365 jours », comme au bandeau
// et sur les cartes du temps 2 ; l'équivalent annuel du moteur ne s'écrit
// qu'ici, dessous, et seulement quand il diffère (« soit … par an »).
//
// Les interdits tenus par ce composant :
// - jamais de « / an » collé à un montant de période (le suffixe dit la période) ;
// - jamais un surcoût sous une pastille verte, et le mot « surcoût » est écrit ;
// - jamais les deux parts sommées visuellement en un total sans décomposition ;
// - jamais un 0 à la place d'une absence : un montant non servi le DIT, avec sa
//   cause, et la puissance recommandée reste servie à côté.
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import {
    lireChiffre,
    lireStructureChoisie,
    severiteEtat,
} from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import { libelleRegimeFiscal } from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    motifDeSurcoutAffichable,
    phraseSeuilDEtat,
} from "@modules/courtage/utils/optim-turpe-v2.recommandation"
import {
    LIBELLE_EQUIVALENT_ANNUEL_CAUSE,
    LIBELLE_ETAT,
    LIBELLE_ETAT_CAUSE,
    LIBELLE_ETAT_MOTIF,
    LIBELLE_MONTANT_NON_SERVI,
    phraseMontantNonServi,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import {
    lireMontantsDeTete,
    lireTitreDuResultat,
    montantEconomiePossible,
    phraseDecompositionDuConseilPrudent,
    phrasePrixDeLaCouverture,
    surLaPeriode,
} from "@modules/courtage/utils/optim-turpe-v2.titre"
import OptimTurpeV2TexteSigne from "./OptimTurpeV2TexteSigne.vue"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"
import {
    classeMontant,
    formatMontantSigne,
    formatNombreSigne,
    motDuMontant,
    suiteColleeAuNombre,
} from "@modules/courtage/utils/optim-turpe-v2.montant"

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            etatInconnu: "État non disponible",
            // Tout libellé vu par le client passe par la table, pour rester
            // relisible d'un coup d'œil au texte-ok (revue C10).
            // Vague wording du 14/10/2026 (Q5, Q12, Q13, Q15, Q21) : aucune
            // puissance n'est dite « recommandée » ni « valable » sous une
            // économie non chiffrée.
            scenarioSaisi: "Vos puissances",
            montantInconnu: "Économie non chiffrée sur cette étude.",
            montantInconnuChoisie:
                "Économie non chiffrée sur cette étude : vos puissances restent affichées.",
            soitParAn: "soit environ {montant} {regime} par an",
            ctaComprise: "contribution tarifaire d'acheminement (CTA) comprise",
            economiePossible: {
                avant: "Économie estimée :",
                apres: "en assumant les dépassements",
                choisir: "Voir les deux scénarios",
            },
            etatDeduit:
                "Cette étude est antérieure à la méthode actuelle : l'état affiché est déduit du montant, avec un seuil de {seuil} €.",
            scenarioLibre:
                "Ce montant est celui de vos puissances, chiffrées telles quelles. Ce n'est pas la recommandation.",
            surcoutNote: "Ce montant est un surcoût, pas une économie.",
        },
    },
})

type Props = {
    etude: OptimTurpeV2Etude
    /**
     * La paire « sans / avec dépassements » tenue par la page (D27). Absente,
     * l'écran garde la forme courante, sans ligne de lecture des dépassements.
     */
    sans?: OptimTurpeV2Etude | null
    avec?: OptimTurpeV2Etude | null
    /**
     * Vague wording du 14/10/2026 (Q17, A7) : sous la carte de tête, qui dit
     * déjà le titre, le montant et sa ligne, le chiffre ne les redit pas ; il
     * ne garde que ce qu'elle ne porte pas (cause de l'équivalent annuel,
     * motif d'un surcoût, état déduit).
     */
    sousLaCarte?: boolean
}
const props = withDefaults(defineProps<Props>(), {
    sans: null,
    avec: null,
    sousLaCarte: false,
})

/** Mène au temps « Choisir », où les deux scénarios se comparent. */
const emit = defineEmits<{ choisir: [] }>()

const getChiffre = computed(() => lireChiffre(props.etude))

const getEtatLabel = computed(() => {
    const etat = getChiffre.value.etat
    return etat ? LIBELLE_ETAT[etat] : t("etatInconnu")
})
const getEtatSeverity = computed(() => severiteEtat(getChiffre.value.etat))

/** Le motif du surcoût, servi par le moteur. Un surcoût sans motif ne s'explique pas. */
const getEtatMotifLabel = computed(() => {
    // DEV-2423 : sur un C5, aucun motif écrit pour les dépassements du C4.
    const motif = motifDeSurcoutAffichable(props.etude)
    return motif ? (LIBELLE_ETAT_MOTIF[motif] ?? null) : null
})

/** La cause d'un état absent, servie par le moteur. */
const getEtatCauseLabel = computed(() => {
    const cause = getChiffre.value.etatCause
    return cause ? (LIBELLE_ETAT_CAUSE[cause] ?? cause) : null
})

/** Le client a imposé sa structure : elle n'est pas « recommandée ». */
const getMontantInconnuCle = computed(() =>
    lireStructureChoisie(props.etude)
        ? "montantInconnuChoisie"
        : "montantInconnu"
)

/** La cause d'un montant non servi (A9) — dite dans la donnée, sans bannière. */
const getMontantNonServiLabel = computed(() => {
    const chiffre = getChiffre.value
    if (chiffre.montantServi !== false) return null
    const cause = chiffre.montantNonServiCause
    // Revue du support du 08/10 (A4) : la mesure servie dit pourquoi. Revue
    // d'ouverture du 12/10 (C9) : une cause encore inconnue de l'écran ne
    // s'affiche jamais en code brut, la phrase générique la remplace.
    return (
        (cause
            ? (phraseMontantNonServi(cause, props.etude.qualite_donnees) ??
              LIBELLE_MONTANT_NON_SERVI[cause])
            : null) ?? t(getMontantInconnuCle.value)
    )
})

/**
 * Le régime, écrit « € HT » comme partout ailleurs sur la page (le moteur sert
 * `HTVA`). Revue du 25/09 : « +296 HTVA par an » n'avait ni le signe € ni la
 * même écriture que la décomposition.
 */
const getRegime = computed(() =>
    libelleRegimeFiscal(getChiffre.value.regimeFiscal)
)

/**
 * Le montant de tête, signé à la convention de la page (revue du 08/10, R1) :
 * « + » le client économise, « − » il paie plus ; vert ou rouge à côté du
 * signe, jamais à sa place.
 */
const getNetteDisplay = computed(() =>
    formatMontantSigne(getChiffre.value.netteEur, getRegime.value)
)
const getNetteClasse = computed(() =>
    classeMontant(getChiffre.value.netteEur, "text-primary-900")
)

/** D27 : la forme du titre, lue sur la paire servie par le moteur. */
const getTitre = computed(() =>
    lireTitreDuResultat(props.etude, props.sans, props.avec)
)
const getEconomiePossible = computed(() =>
    getTitre.value.forme === "economie_possible" ? getTitre.value : null
)

/** Le montant de la période, et l'équivalent annuel du moteur s'il diffère. */
const getMontants = computed(() => lireMontantsDeTete(getChiffre.value))
/**
 * Maquette allégée : la période ne s'écrit qu'aux paramètres ; le montant
 * garde seulement sa durée (« sur 365 jours »), jamais les dates. Revue du
 * support du 08/10 (A2) : le mot du sens suit le montant, « +632 € HT
 * d'économie sur 365 jours », « −246 € HT de surcoût sur 365 jours ».
 */
/**
 * « d'économie par an », collé au montant par un espace insécable (N3) : la
 * suite part avec le nombre, seule une période longue passe à la ligne.
 */
const getLibellePeriode = computed(() =>
    suiteColleeAuNombre(
        motDuMontant(getChiffre.value.netteEur),
        surLaPeriode(getChiffre.value.periode.jours, getChiffre.value.periode)
    )
)
/** « soit +2 900 € HT par an » : seulement quand il diffère du montant affiché. */
const getSoitParAn = computed(() => {
    const { annuelEur, unSeul } = getMontants.value
    // Q15 : sur 365 ou 366 jours, le montant se dit déjà « par an ».
    const parAn = surLaPeriode(getChiffre.value.periode.jours) === "par an"
    return annuelEur === null || unSeul || parAn
        ? null
        : t("soitParAn", {
              montant: formatNombreSigne(annuelEur),
              regime: getRegime.value,
          })
})
const getCtaComprise = computed(
    () => getChiffre.value.reconciliation?.cta_incluse === true
)
/**
 * Le seuil qui a décidé l'état (DEV-2183) : sous une économie, il vit sous
 * « D'où vient ce montant » (maquette allégée) ; sous les autres états
 * (déjà optimale, surcoût), il EXPLIQUE l'état et reste à côté du chiffre.
 */
const getInegalite = computed(() =>
    getChiffre.value.etat === "economie" ? null : phraseSeuilDEtat(props.etude)
)
const getEquivalentAnnuelCauseLabel = computed(() => {
    const chiffre = getChiffre.value
    if (chiffre.equivalentAnnuel) return null
    const cause = chiffre.equivalentAnnuelCause
    return cause ? (LIBELLE_EQUIVALENT_ANNUEL_CAUSE[cause] ?? cause) : null
})
</script>

<template>
    <section
        class="optim-turpe-v2-chiffre flex flex-col gap-3"
        data-testid="optim-turpe-v2-chiffre"
    >
        <!-- D27, MAQUETTE 2 BIS : le conseil prudent coûte plus cher qu'aujourd'hui
             alors que l'autre scénario est rentable. Le titre dit ce qu'il y a à
             gagner et à quelle condition ; le prix de la tranquillité suit, dans
             la donnée, sans encadré d'avertissement. -->
        <template v-if="getEconomiePossible && !sousLaCarte">
            <!-- A7 (14/10/2026) : le titre dit déjà « Économie estimée » ;
                 plus de pastille qui le redouble. -->
            <p
                class="text-lg font-semibold text-primary-700"
                data-testid="optim-turpe-v2-titre-economie-possible"
            >
                {{ t("economiePossible.avant") }}
                <span :class="classeMontant(getEconomiePossible.possibleEur)">{{
                    montantEconomiePossible(getEconomiePossible)
                }}</span>
                {{ t("economiePossible.apres") }}
            </p>
            <p
                class="rounded-r-md border-l-2 border-surface-200 bg-surface-50 px-3 py-2 text-sm text-primary-800"
            >
                {{ phrasePrixDeLaCouverture(getEconomiePossible) }}
                <button
                    type="button"
                    class="font-medium text-primary-800 underline underline-offset-2"
                    data-testid="optim-turpe-v2-choisir"
                    @click="emit('choisir')"
                >
                    {{ t("economiePossible.choisir") }}
                </button>
            </p>
            <p class="text-xs text-primary-500">
                <OptimTurpeV2TexteSigne
                    :texte="
                        phraseDecompositionDuConseilPrudent(getEconomiePossible)
                    "
                />
            </p>
        </template>

        <template v-else-if="sousLaCarte">
            <p
                v-if="getNetteDisplay !== null && getEquivalentAnnuelCauseLabel"
                class="text-xs text-primary-500"
            >
                {{ getEquivalentAnnuelCauseLabel }}
            </p>
            <p
                v-if="getChiffre.estSurcout && getEtatMotifLabel"
                class="text-xs text-primary-600"
            >
                {{ getEtatMotifLabel }}
            </p>
            <p
                v-if="
                    !getChiffre.etatServiParLeMoteur && getChiffre.etat !== null
                "
                class="text-xs text-primary-400"
            >
                {{ t("etatDeduit", { seuil: getChiffre.seuilEur }) }}
            </p>
        </template>

        <template v-else>
            <!-- Une économie se lit déjà au bandeau (« Économie identifiée ») :
                 la pastille d'état ne reste que pour les autres états
                 (surcoût, déjà optimale…), qui changent la lecture du chiffre. -->
            <div
                v-if="
                    getChiffre.etat !== 'economie' ||
                    getChiffre.estScenarioLibre
                "
                class="flex flex-wrap items-center gap-2"
            >
                <BaseTag
                    v-if="getChiffre.etat !== 'economie'"
                    :severity="getEtatSeverity"
                    :value="getEtatLabel"
                    class="w-fit"
                />
                <!-- Scénario imposé : dit AVANT le chiffre, pour qu'il ne se lise
                     jamais comme la recommandation du moteur (§5). -->
                <BaseTag
                    v-if="getChiffre.estScenarioLibre"
                    severity="info"
                    :value="t('scenarioSaisi')"
                    class="w-fit"
                />
            </div>

            <!-- LE CHIFFRE : le montant de la période analysée avec sa durée,
                 « +2 821 € HT sur 365 jours », la même unité qu'au bandeau et
                 sur les cartes. L'équivalent annuel du moteur ne s'écrit que
                 s'il diffère, dessous, « soit … par an ». -->
            <div
                v-if="getNetteDisplay !== null"
                class="flex flex-col gap-y-1"
                data-testid="optim-turpe-v2-montants"
            >
                <!-- N3 (19/10/2026) : « € HT » reste attaché au nombre, et la
                     suite (« d'économie par an ») part avec lui sur la même
                     ligne : un texte courant, sans flex-wrap qui ferait passer
                     la suite seule à la ligne. -->
                <p data-testid="optim-turpe-v2-montant-ligne">
                    <span
                        class="whitespace-nowrap text-3xl font-semibold"
                        :class="getNetteClasse"
                        data-testid="optim-turpe-v2-montant-tete"
                        >{{ getNetteDisplay }}</span
                    ><span
                        class="text-base text-primary-700"
                        data-testid="optim-turpe-v2-montant-suite"
                        >{{ getLibellePeriode }}</span
                    >
                </p>
                <p
                    v-if="getSoitParAn"
                    class="text-sm text-primary-600"
                    data-testid="optim-turpe-v2-soit-par-an"
                >
                    {{ getSoitParAn }}
                </p>
            </div>

            <!-- Montant non servi : la cause, dans la donnée. Pas de 0, pas de vide. -->
            <p
                v-else-if="getMontantNonServiLabel"
                class="text-sm font-medium text-primary-800"
            >
                {{ getMontantNonServiLabel }}
            </p>
            <p v-else class="text-sm font-medium text-primary-800">
                {{ getEtatCauseLabel ?? t(getMontantInconnuCle) }}
            </p>

            <!-- Équivalent annuel non calculé : la cause servie par le moteur. -->
            <p
                v-if="getNetteDisplay !== null && getEquivalentAnnuelCauseLabel"
                class="text-xs text-primary-500"
            >
                {{ getEquivalentAnnuelCauseLabel }}
            </p>

            <!-- Ce que contient le montant (D27, maquette 2). -->
            <p
                v-if="getNetteDisplay !== null && getCtaComprise"
                class="text-xs text-primary-600"
                data-testid="optim-turpe-v2-cta-comprise"
            >
                {{ t("ctaComprise") }}
            </p>

            <p
                v-if="getChiffre.estSurcout"
                class="text-xs text-[var(--p-red-700)]"
            >
                {{ t("surcoutNote") }}
                <span v-if="getEtatMotifLabel"> {{ getEtatMotifLabel }}</span>
            </p>
            <p v-else-if="getEtatMotifLabel" class="text-xs text-primary-600">
                {{ getEtatMotifLabel }}
            </p>
            <p
                v-if="getInegalite"
                class="text-xs text-primary-500"
                data-testid="optim-turpe-v2-inegalite"
            >
                {{ getInegalite }}
            </p>

            <p
                v-if="getChiffre.estScenarioLibre"
                class="text-xs text-primary-600"
            >
                {{ t("scenarioLibre") }}
            </p>

            <!-- L'état est-il servi par le moteur ? Le dire vaut mieux que de laisser
                 croire que le moteur l'a tranché (repli explicite, étude ancienne). -->
            <p
                v-if="
                    !getChiffre.etatServiParLeMoteur && getChiffre.etat !== null
                "
                class="text-xs text-primary-400"
            >
                {{ t("etatDeduit", { seuil: getChiffre.seuilEur }) }}
            </p>
        </template>
    </section>
</template>
